/**
 * useShopFilterMeta — sidebar seed for the shop list.
 *
 * One PUBLIC fetch on mount: categories (with sellable product
 * counts) + the catalog-wide price range (paisa) for the slider
 * bounds. No retries / refetch — these change rarely; the page can
 * call reload if needed.
 */

import { useCallback, useEffect, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface ShopCategoryDto {
  readonly id: string
  readonly name: string
  readonly product_count: number
}

export interface ShopPriceRangeDto {
  readonly min_paisa: number | null
  readonly max_paisa: number | null
}

interface Body {
  readonly success: true
  readonly data: {
    readonly categories: ShopCategoryDto[]
    readonly priceRange: ShopPriceRangeDto
  }
}

export interface UseShopFilterMetaReturn {
  readonly categories: ShopCategoryDto[]
  readonly priceRange: ShopPriceRangeDto
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

export function useShopFilterMeta(): UseShopFilterMetaReturn {
  const [categories, setCategories] = useState<ShopCategoryDto[]>([])
  const [priceRange, setPriceRange] = useState<ShopPriceRangeDto>({
    min_paisa: null,
    max_paisa: null
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(`${APIBASE}${APIROUTES.SHOPFILTERMETA}`, { signal: ac.signal })
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) {
          setCategories(body.data.categories)
          setPriceRange(body.data.priceRange)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { categories, priceRange, isLoading, error, reload }
}
